import { DEFAULT_LOCALE, SUPPORTED_CODES, LOCALES } from './locales'

// How the active locale is chosen when nothing better is known.
//
// This is the fallback chain, not the primary source. A site whose pages are built
// under a locale prefix knows each page's language at build time and should say so
// explicitly, by cloning the instance per page from `pageContext.lang` — see
// celebrando-site's gatsby-ssr. That is both more correct and free of the mismatch
// this chain can cause: `initI18n` runs once on import, and during SSR there is no
// document to read a cookie from, so a cookie-derived language would differ between
// the HTML we build and what the browser then renders.
//
// What is left for this chain is everything else: a site with one language per build
// resolves through GATSBY_LANG and lands on its own language, which is what every
// site other than Celebrando does today.
const LANGUAGE_SOURCE_ENABLED = true

// Map an arbitrary language string (cookie value, env, 'en', 'es-419', …) onto
// a supported locale code, or null if unsupported.
function normalizeToSupported(value: string | null | undefined): string | null {
  if (!value) return null
  const v = value.toLowerCase()
  // exact code match
  const exact = SUPPORTED_CODES.find(c => c.toLowerCase() === v)
  if (exact) return exact
  // base-language match (e.g. 'es' or 'es-XX' → the es-* locale)
  const baseOnly = v.split(/[-_]/)[0]
  const byBase = LOCALES.find(l => l.base === baseOnly)
  return byBase ? byBase.code : null
}

// --- providers (ordered; first non-null wins) --------------------------------
// Add providers (route param, user profile, CMS lang, …) here — the mechanism
// never changes, only this list.

function fromCookie(): string | null {
  if (typeof document === 'undefined') return null
  const m = document.cookie.match(/(?:^|;\s*)db-lang-pref=([^;]+)/)
  return normalizeToSupported(m ? decodeURIComponent(m[1]) : null)
}

function fromBuildEnv(): string | null {
  return normalizeToSupported(process.env.GATSBY_LANG)
}

const PROVIDERS: Array<() => string | null> = [fromCookie, fromBuildEnv]

export function resolveLocale(): string {
  if (!LANGUAGE_SOURCE_ENABLED) return DEFAULT_LOCALE
  for (const provider of PROVIDERS) {
    const code = provider()
    if (code) return code
  }
  return DEFAULT_LOCALE
}
